/* eslint-disable indent */
import { Box, Card, Grid, List, ListItem, TextField, Typography } from '@mui/material';
import React from 'react';
import { Trans } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { AVATAR_SHADOW, AVATAR_SIZE, AVATAR_VARIANT } from 'src/components/Avatar/types';
import { Button } from 'src/components/Button';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import colors from 'src/theme/base/colors';
import { ConfirmFacebookPostScheduleProps } from './types';
import boxShadows from 'src/theme/base/boxShadows';
import { useLabels } from 'src/hooks/useLabels';
import { TYPOGRAPHY_COLORS, TYPOGRAPHY_WEIGHT } from 'src/components/Typography/types';
export const ConfirmFacebookPostSchedule: React.FC<ConfirmFacebookPostScheduleProps> = ({
  handlePreviousButtonClick,
  postSchedule,
  handleMoveToNextStep,
  fbPosts,
  setSchedulePosts,
  scheduleFacebookPost,
}) => {
  const { handleFrequencyLabels, handlePostTypeLabels } = useLabels();

  const {
    frequency,
    startTime,
    startDate,
    endDate,
    name,
    city,
    postType,
    status,
    jobId,
    pageData,
    groupData,
    facebookPosts,
  } = postSchedule;

  const details = {
    facebookPosts,
    frequency,
    startDate,
    endDate,
    startTime,
    jobId,
    postType,
    status,
    pageData,
    groupData,
  };

  const saveDetails = {
    ...details,
    name: name ?? '',
    city: city ?? '',
  };

  const handleSubmit = () => {
    if (jobId) {
      scheduleFacebookPost({
        variables: {
          postSchedule: {
            ...details,
            jobId,
          },
        },
      });

      setSchedulePosts(prevPosts => [...prevPosts, saveDetails]);
    }

    handleMoveToNextStep();
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box mb={4} display="flex" justifyContent="center">
          <Typography variant="h5">
            <Trans>Facebook Posztolás - Összegzés</Trans>
          </Typography>
        </Box>
        {fbPosts.map((fbPost, index) => (
          <Grid item xs={12} key={fbPost.id}>
            <Box
              width="100%"
              border={`2px solid ${colors.inputColors.borderColor.main}`}
              borderRadius={3}
              boxShadow={boxShadows.sm}
              mb={3}
              sx={{
                bgcolor: colors.grey[100],
                p: 2,
              }}>
              <Card
                key={index}
                sx={{
                  border: `1px solid ${colors.inputColors.borderColor.main}`,
                  width: 45,
                  height: 45,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '25%',
                  backgroundColor: '#fffff',
                  boxShadow: boxShadows.sm,
                  mb: 2,
                  p: 1,
                }}
                variant="elevation">
                <Typography
                  sx={{ fontSize: 14 }}
                  fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                  color={TYPOGRAPHY_COLORS.TEXT}>
                  #{facebookPosts[index]?.postOrder}
                </Typography>
              </Card>

              <Box
                border={`2px solid ${colors.inputColors.borderColor.main}`}
                borderRadius={3}
                sx={{
                  p: 2,
                  fontSize: 13,
                  bgcolor: 'white !important',
                  lineHeight: 1.5,
                }}>
                <TextField
                  fullWidth
                  multiline
                  aria-readonly
                  variant="outlined"
                  inputProps={{
                    style: {
                      minWidth: '100%',
                      maxHeight: '100%',
                      fontSize: 13,
                      lineHeight: 1.5,
                      borderRadius: 0,
                    },
                    readOnly: true,
                  }}
                  sx={{
                    '.MuiOutlinedInput-root': {
                      border: 0,
                      padding: '0 !important',
                      margin: 0,
                    },
                    '.MuiInputBase-root': {
                      backgroundColor: 'white !important',
                      padding: '0 !important',
                      margin: 0,
                    },
                  }}
                  minRows={1}
                  value={fbPost.postText}
                />
              </Box>
              <Box mt={2} sx={{ display: 'flex', justifyContent: 'right' }}>
                <Avatar
                  alt="Generated image"
                  src={fbPost.postPictureUrl}
                  variant={AVATAR_VARIANT.ROUNDED}
                  size={AVATAR_SIZE.XXL}
                  shadow={AVATAR_SHADOW.SM}
                  onClick={() => {
                    window.open(fbPost.postPictureUrl, '_blank', 'noopener noreferrer');
                  }}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'transform 0.4s',
                    '&:hover': {
                      transform: 'scale(1.04)',
                    },
                  }}
                />
              </Box>
            </Box>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            width="100%"
            border={`2px solid ${colors.inputColors.borderColor.main}`}
            borderRadius={3}
            boxShadow={boxShadows.sm}
            mb={3}
            sx={{
              bgcolor: colors.grey[100],
              p: 2,
            }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              <Trans>Mely oldalakra posztoljunk:</Trans>
            </Typography>
            <Box>
              <List sx={{ padding: 2 }}>
                {Object.values(postSchedule?.pageData).map((value, index) => (
                  <ListItem key={index} style={{ display: 'list-item', listStyleType: 'disc' }}>
                    <Typography variant="body2">{value.name}</Typography>
                  </ListItem>
                ))}
              </List>
            </Box>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              <Trans>Mely csoportokra posztoljunk:</Trans>
            </Typography>
            <Box>
              <List sx={{ padding: 2 }}>
                {Object.values(postSchedule?.groupData).map((value, index) => (
                  <ListItem key={index} style={{ display: 'list-item', listStyleType: 'disc' }}>
                    <Typography variant="body2">{value.name}</Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            width="100%"
            border={`2px solid ${colors.inputColors.borderColor.main}`}
            borderRadius={3}
            boxShadow={boxShadows.sm}
            sx={{
              bgcolor: colors.grey[100],
              p: 2,
            }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              <Trans>Ütemezési beállítások:</Trans>
            </Typography>
            <Box>
              <Typography variant="body2">
                <strong>Posztolás módja:</strong>{' '}
                {handlePostTypeLabels(postSchedule?.postType) || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Kezdési dátum:</strong> {postSchedule?.startDate || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Kezdési időpont:</strong> {postSchedule?.startTime || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Gyakoriság:</strong>{' '}
                {handleFrequencyLabels(postSchedule?.frequency) || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button onClick={handlePreviousButtonClick}>
              <Trans>generic.back</Trans>
            </Button>
            <Button
              onClick={handleSubmit}
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.DARK}
              type="submit">
              <Trans>Mentés</Trans>
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};
